import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../shared/api';

type Locale = 'hr' | 'en';
type Emp = { id: number; first_name: string | null; last_name: string | null; department_name: string | null; region: string | null; company_name: string | null };

const filt: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.84rem' };

/**
 * Reusable odabir zaposlenika — quick search + brzi filteri (firma / regija / poslovnica).
 * Koristi se gdje god se bira zaposlenik (Dosje, Bolovanja, Raspored…).
 */
export function EmployeePicker({ token, locale, value, onChange }: {
    token: string; locale: Locale; value: number | null; onChange: (id: number | null) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [emps, setEmps] = useState<Emp[] | null>(null);
    const [search, setSearch] = useState('');
    const [region, setRegion] = useState('');
    const [company, setCompany] = useState('');
    const [dept, setDept] = useState('');

    useEffect(() => {
        api<{ employees: Emp[] }>('/hr/employees', token).then((r) => setEmps(r.employees)).catch(() => setEmps([]));
    }, [token]);

    const regions = useMemo(() => Array.from(new Set((emps ?? []).map((e) => e.region).filter(Boolean))).sort() as string[], [emps]);
    const companies = useMemo(() => Array.from(new Set((emps ?? []).map((e) => e.company_name).filter(Boolean))).sort() as string[], [emps]);
    const departments = useMemo(() => Array.from(new Set((emps ?? []).map((e) => e.department_name).filter(Boolean))).sort() as string[], [emps]);

    const filtered = useMemo(() => {
        let f = emps ?? [];
        if (search) { const s = search.toLowerCase(); f = f.filter((e) => `${e.last_name ?? ''} ${e.first_name ?? ''}`.toLowerCase().includes(s)); }
        if (company) f = f.filter((e) => e.company_name === company);
        if (region) f = f.filter((e) => e.region === region);
        if (dept) f = f.filter((e) => e.department_name === dept);
        return f;
    }, [emps, search, company, region, dept]);

    const name = (e: Emp) => `${e.last_name ?? ''} ${e.first_name ?? ''}`.trim() || `#${e.id}`;

    return (
        <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <input style={{ ...filt, minWidth: 200, flex: '1 1 200px' }} placeholder={t('Pretraži zaposlenika…', 'Search employee…')} value={search} onChange={(e) => setSearch(e.target.value)} />
                <select style={filt} value={company} onChange={(e) => setCompany(e.target.value)}>
                    <option value="">{t('Sve firme', 'All companies')}</option>{companies.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select style={filt} value={region} onChange={(e) => setRegion(e.target.value)}>
                    <option value="">{t('Sve regije', 'All regions')}</option>{regions.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select style={filt} value={dept} onChange={(e) => setDept(e.target.value)}>
                    <option value="">{t('Sve poslovnice', 'All branches')}</option>{departments.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>
            <select style={{ ...filt, width: '100%', maxWidth: 520 }} value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
                <option value="">{emps === null ? t('Učitavam…', 'Loading…') : t(`— odaberi (${filtered.length}) —`, `— select (${filtered.length}) —`)}</option>
                {filtered.map((e) => <option key={e.id} value={e.id}>{name(e)}{e.department_name ? ` · ${e.department_name}` : ''}</option>)}
            </select>
        </div>
    );
}
